import React, { useEffect, useState } from "react";
import { Grid, Cell } from "react-md";
import SchedulingAssistantSummaryCard from "./SchedulingAssistantSummaryCard";
import { ClockInOutCard, ProductionTimeCard } from "components/Shared/TimeLogs";
import { CaseTimeLogWorkTypeEnum } from "interfaces/Mueller.Business.Models";
import { getPayPeriodStartDate, getPayPeriodEndDate } from "components/EmployeeTimeLogs";
import { getTimeClockByPayPeriod, addTimeClock, deleteTimeClock, updateTimeClock } from "actions/account";
import { getCaseTimeLogByUserPayPeriod, updateCaseTimeLog, deleteCaseTimeLog, addCaseTimeLog } from "actions/casetimelog";
import { getTimeLogByDaySummary } from "actions/qualityAssurance";
import { connect } from "react-redux";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import SAPerformanceLog from "./SAPerformanceLog";
import { MANAGEMENT_CALENDAR_EDIT } from 'typeConstants/rights';
import { userHasRight } from "selectors/rights";
import EmployeeCalendar from "components/Employee/EmployeeCalendar.tsx";
import { MCard } from "components/Shared";

interface PayPeriodViewModel {
  id: number;
  fromDate: Date;
  toDate: Date;
  fromToDisplay: string;
  closedDate?: Date;
};

interface SelectedEmployee {
  internetUserId: number,
  employeeId: number, 
  name: { firstLast: string },
  number: number,
  isTeamLeader: boolean,
};

interface Props {
  selectedPayPeriod: PayPeriodViewModel,
  selectedEmployee: SelectedEmployee,

  timeClockByPayPeriod,
  isFetchingTimeClockByPayPeriod,
  getTimeClockByPayPeriod,
  addTimeClock,
  isAddingTimeClock,
  isDeletingTimeClock,
  isUpdatingTimeClock,
  deleteTimeClock,
  updateTimeClock,

  timeLogs,
  isFetchingTimeLogs,
  updateCaseTimeLog,
  isUpdatingTimeLogs,
  isDeletingTimeLogs,
  deleteCaseTimeLog,
  addCaseTimeLog,
  isAddingTimeLogs,
  getCaseTimeLogByUserPayPeriod,
  canEditCalendarAsManager,
};

const SchedulingAssistantTimelogDetails = ({
  selectedPayPeriod,
  selectedEmployee,

  timeClockByPayPeriod,
  isFetchingTimeClockByPayPeriod,
  getTimeClockByPayPeriod,
  addTimeClock,
  isAddingTimeClock,
  isDeletingTimeClock,
  isUpdatingTimeClock,
  deleteTimeClock,
  updateTimeClock,

  timeLogs,
  isFetchingTimeLogs,
  updateCaseTimeLog,
  isUpdatingTimeLogs,
  isDeletingTimeLogs,
  deleteCaseTimeLog,
  addCaseTimeLog,
  isAddingTimeLogs,
  getCaseTimeLogByUserPayPeriod,
  canEditCalendarAsManager,
}: Props) => {
  const [refreshSummaryCard, setRefreshSummaryCard] = useState<Date>(null);
  const [filterDate, setFilterDate] = useState<Date>();

  const onRefreshSummary = () => setRefreshSummaryCard(new Date());
  const payPeriodStartDate = getPayPeriodStartDate(selectedPayPeriod.fromDate);
  const payPeriodEndDate = getPayPeriodEndDate(selectedPayPeriod.toDate);
  
  useEffect(() => {
    setFilterDate(null);
  }, [selectedEmployee, selectedPayPeriod]);

  return (
    <>
      <Cell size={12} style={{ marginTop: 0, paddingTop: 0 }}>
        <SchedulingAssistantSummaryCard
          internetUserId={selectedEmployee?.internetUserId}
          employeeName={selectedEmployee?.name.firstLast}
          payPeriodId={selectedPayPeriod?.id}
          refreshCard={refreshSummaryCard}
          onDateClicked={setFilterDate}
        />
      </Cell>
      <Cell size={6} className="card-style clock-in-out-card">
        <ClockInOutCard
          displayClockInOutButton={false}
          data={timeClockByPayPeriod}
          isFetchingData={isFetchingTimeClockByPayPeriod}
          payPeriod={selectedPayPeriod}
          getData={getTimeClockByPayPeriod}
          isEditable
          addTimeClock={addTimeClock}
          internetUserId={selectedEmployee.internetUserId}
          isAddingTimeClock={isAddingTimeClock}
          isDeletingTimeClock={isDeletingTimeClock}
          isUpdatingTimeClock={isUpdatingTimeClock}
          deleteTimeClock={deleteTimeClock}
          updateTimeClock={updateTimeClock}
          startDate={payPeriodStartDate}
          endDate={payPeriodEndDate}
          refreshSummaryCard={onRefreshSummary}
        />
      </Cell>
      <EmployeeTEAM internetUserId={selectedEmployee?.internetUserId} cellWidth={6} />
      <SAPerformanceLog payPeriodId={selectedPayPeriod?.id} internetUserId={selectedEmployee?.internetUserId} filterDate={filterDate} />
      <MCard
        title="Employee Calendar"
        cellWidth={12}
        fields={[
          {
            key: "calendar", 
            control: (
              <EmployeeCalendar 
                employeeId={selectedEmployee.employeeId}
                readOnly={!canEditCalendarAsManager}
                allowManagementEdit={canEditCalendarAsManager}
              />)
          }]}
      />       
    </>
  );
};

const mapStateToProps = state => {
  return {
    isFetchingTimeLogs: state.caseTimeLog.isFetchingTimeLogs,
    timeLogs: state.caseTimeLog.timeLogs,
    timeClockByPayPeriod: state.user.timeClockByPayPeriod,
    isFetchingTimeClockByPayPeriod: state.user.isFetchingTimeClockByPayPeriod,
    isAddingTimeClock: state.user.isAddingTimeClock,
    isDeletingTimeClock: state.user.isDeletingTimeClock,
    isUpdatingTimeClock: state.user.isUpdatingTimeClock,
    isUpdatingTimeLogs: state.caseTimeLog.isUpdatingTimeLogs,
    isDeletingTimeLogs: state.caseTimeLog.isDeletingTimeLogs,
    isAddingTimeLogs: state.caseTimeLog.fetching,
    timeLogByDaySummary: state.qualityAssurance.timeLogByDaySummary,
    isFetchingTimeLogByDaySummary: state.qualityAssurance.isFetchingTimeLogByDaySummary,
    canEditCalendarAsManager: userHasRight(MANAGEMENT_CALENDAR_EDIT)(state),
  };
};
const mapDispatchToProps = dispatch => ({
  getCaseTimeLogByUserPayPeriod(internetUserId, payPeriodId) {
    dispatch(getCaseTimeLogByUserPayPeriod(internetUserId, payPeriodId));
  },
  getTimeClockByPayPeriod(internetUserId, payPeriodId) {
    dispatch(getTimeClockByPayPeriod(internetUserId, payPeriodId));
  },
  addTimeClock(internetUserId, dto) {
    dispatch(addTimeClock(internetUserId, dto));
  },
  deleteTimeClock(id) {
    dispatch(deleteTimeClock(id));
  },
  updateTimeClock(id, clockIn, clockOut) {
    dispatch(updateTimeClock(id, clockIn, clockOut));
  },
  updateCaseTimeLog(updatedCaseTimeLog, updateTimeLogFunc) {
    dispatch(updateCaseTimeLog(updatedCaseTimeLog, updateTimeLogFunc));
  },
  deleteCaseTimeLog(id) {
    dispatch(deleteCaseTimeLog(id));
  },
  addCaseTimeLog(dto) {
    dispatch(addCaseTimeLog(dto));
  },
  getTimeLogByDaySummary(internetUserId, payPeriodId) {
    dispatch(getTimeLogByDaySummary(internetUserId, payPeriodId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SchedulingAssistantTimelogDetails);

